/* global YT, Vimeo */

import { enqueueYT } from '@site-module/embed';
import { getYouTubeID } from '@site-module/util';

export default function initShowcaseVideo() {
    document.querySelectorAll('.showcaseplayer').forEach(parent => {
        const url = parent.dataset.videoUrl;
        const showcase_wrapper = document.getElementById('showcase') ?? parent.parentNode;
        const controlButton = showcase_wrapper.querySelector('.video-control');
        let video;
        let isPlaying = false;
        let player;
        let show_video_on_mobile = parent.dataset.videoOnMobile.toLowerCase() === 'true';
        let aspect_ratio = 16 / 9;

        const playerDimensions = {
            width: parent.clientWidth,
            height: Math.round(parent.clientWidth / 16 * 9)
        };

        aspect_ratio = showcase_wrapper.clientHeight / showcase_wrapper.clientWidth;

        if (!show_video_on_mobile && aspect_ratio.toFixed(1) >= 0.7) {
            return;
        }

        function scaleVideo() {
            if (!video) {
                return;
            }

            let parent_aspect = parent.clientWidth / parent.clientHeight;
            let vid_aspect = 16 / 9;

            if (parent_aspect > vid_aspect) {
                // parent is landscape-ishier than the video
                video.style.transform = 'scale(' + parent.getBoundingClientRect().width / video.clientWidth + ')';
            } else {
                video.style.transform = 'scale(' + parent.getBoundingClientRect().height / video.clientHeight + ')';
            }
        }
        function muteYoutube(event) {
            event.target.mute();
        }

        const updateControlUi = () => {
            if (!controlButton) {
                return;
            }

            const playIcon = controlButton.querySelector('.video-control__icon--play');
            const pauseIcon = controlButton.querySelector('.video-control__icon--pause');

            if (isPlaying) {
                controlButton.ariaPressed = 'true';
                pauseIcon.ariaHidden = 'false';
                playIcon.ariaHidden = 'true';
            } else {
                controlButton.ariaPressed = 'false';
                pauseIcon.ariaHidden = 'true';
                playIcon.ariaHidden = 'false';
            }
        };

        function handleYTState(event) {
            let YTP = event.target;
            let rewindTO;

            if (event.data === YT.PlayerState.PLAYING){
                const remains = YTP.getDuration() - YTP.getCurrentTime();

                if (rewindTO) {
                    clearTimeout(rewindTO);
                }

                // To remove black screen end of the loop videos - rewind the video with seeking to a frame.
                rewindTO = setTimeout(function(){
                    YTP.seekTo(0);
                }, (remains - 0.3) * 1000);
                parent.classList.add('playing');
                isPlaying = true;
                updateControlUi();
                video = parent.children[0];
                scaleVideo();
            }

            if (event.data === YT.PlayerState.PAUSED) {
                isPlaying = false;
                updateControlUi();
            }
        }

        function toggleVimeo(vimPlayer) {
            if (isPlaying) {
                vimPlayer.pause();
            } else {
                vimPlayer.play();
            }
        }

        function toggleYouTube(ytPlayer) {
            if (isPlaying) {
                ytPlayer.pauseVideo();
                isPlaying = false;
            } else {
                ytPlayer.playVideo();
                isPlaying = true;
            }
        }

        function startVimeo() {
            // vimeo inserts the player in the placeholder
            parent.querySelector('.placeholder').remove();
            player = new Vimeo.Player(parent, {
                ...playerDimensions,
                url: url,
                background: true,
                byline: false,
                muted: true,
                title: false
            });
            
            player.on('play', function() {
                parent.classList.add('playing');
                video = parent.children[0];
                isPlaying = true;
                updateControlUi();
                scaleVideo();
            });

            player.on('error', function() {
                parent.remove();
            });

            player.on('pause', function() {
                if (controlButton) {
                    isPlaying = false;
                    updateControlUi();
                }

                // SM - This pause callback totally removes the video from the DOM
                // when we click on our manual pause control.
                // autoplay is blocked by the browser so remove the player
                if (!controlButton) {
                    parent.remove();
                    isPlaying = false;
                }
            });

            controlButton?.addEventListener('click', () => toggleVimeo(player));
            controlButton?.addEventListener('keydown', function(event) {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    toggleVimeo(player);
                }
            });
        }

        window.addEventListener('resize', () => {
            scaleVideo();
        });

        if (url.includes('vimeo')) {
            if (typeof Vimeo !== 'undefined') {
                startVimeo();
            } else {
                const tag = document.head.querySelector('[src="https://player.vimeo.com/api/player.js"]');
                if (! tag) {
                    const script = document.createElement('script');
                    script.setAttribute('src', 'https://player.vimeo.com/api/player.js');
                    script.onload = startVimeo;
                    document.head.appendChild(script);
                } else {
                    tag.addEventListener('load', startVimeo);
                }
            }

        } else {
            const tag = document.head.querySelector('[src="https://www.youtube.com/iframe_api"]');
            if (! tag) {
                const script = document.createElement('script');
                script.setAttribute('src', 'https://www.youtube.com/iframe_api');
                document.head.appendChild(script);
            }
            enqueueYT(() => {
                const ytPlayer = new YT.Player(parent.querySelector('.placeholder'), {
                    ...playerDimensions,
                    videoId: getYouTubeID(url),
                    events: {
                        'onReady': muteYoutube,
                        'onError': parent.remove,
                        'onStateChange': handleYTState
                    },
                    playerVars: {
                        autoplay: 1,
                        controls: 0,
                        iv_load_policy: 3,
                        modestbranding: 1,
                        playsinline: 1,
                        loop: 1,
                        playlist: getYouTubeID(url)
                    }
                });

                controlButton?.addEventListener('click', () => toggleYouTube(ytPlayer));

                controlButton?.addEventListener('keydown', function(event) {
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        toggleYouTube(ytPlayer);
                    }
                });
            });
        }
    });
}
